import { Link } from "react-router-dom";
import {
    ArrowUpRight, BrainCircuit, Users, Boxes, Sparkles, Workflow, FileSearch, Bot,
    ShieldCheck, Rocket, Gauge, Building2, HeartPulse, ShoppingBag, Truck, Factory,
    Banknote, Check, Quote,
} from "lucide-react";
import PageMeta from "../components/layout/PageMeta";
import HeroAIVisual from "../components/sections/HeroAIVisual";
import ParticleField from "../components/effects/ParticleField";
import FinalCTA from "../components/sections/FinalCTA";
import WorkflowSimulator from "../components/home/WorkflowSimulator";
import BeforeAfter from "../components/home/BeforeAfter";

const pillars = [
    {
        icon: <BrainCircuit className="w-5 h-5" />,
        title: "AI Solutions",
        kicker: "Production AI · not pilots",
        description:
            "We design and ship generative, agentic and document-intelligence systems that live inside your operations — measured, monitored and owned.",
        to: "/ai-solutions",
        chips: ["Generative AI", "Agents", "Copilots", "Doc Intelligence", "MLOps"],
    },
    {
        icon: <Users className="w-5 h-5" />,
        title: "Talent Solutions",
        kicker: "AI-assisted specialised hiring",
        description:
            "Vetted AI, data and engineering talent — sourced, scored and shortlisted in hours using our TalentIQX engine. Built for enterprises and staffing partners.",
        to: "/talent-solutions",
        chips: ["AI Matching", "IT Staffing", "Managed Recruiting", "C2C / W2"],
    },
    {
        icon: <Boxes className="w-5 h-5" />,
        title: "Product Engineering",
        kicker: "Senior squads · cloud-native",
        description:
            "SaaS products, enterprise portals, AI dashboards and data platforms — designed, built and operated by senior-led squads on modern foundations.",
        to: "/product-engineering",
        chips: ["SaaS", "Cloud", "Data", "AI Dashboards", "UX/UI"],
    },
];

const accelerators = [
    { slug: "talentiqx", name: "Talent intelligence", tag: "TalentIQX", copy: "Score, rank, and shortlist candidates using explainable AI models.", icon: <Users className="w-5 h-5" /> },
    { slug: "docuiqx", name: "Document intelligence", tag: "DocuIQX", copy: "Extract structured data from scanned loan documents, claims, and invoices.", icon: <FileSearch className="w-5 h-5" /> },
    { slug: "agentiqx", name: "Enterprise agents", tag: "AgentIQX", copy: "Deploy policy-governed agents that automate system lookups and actions.", icon: <Bot className="w-5 h-5" /> },
    { slug: "recruitiqx", name: "AI dashboards & workflows", tag: "RecruitIQX", copy: "Orchestrate processes, tracking, and approvals in custom interfaces.", icon: <Workflow className="w-5 h-5" /> },
];

const industries = [
    { slug: "healthcare", name: "Healthcare & Life Sciences", icon: <HeartPulse className="w-4 h-4" /> },
    { slug: "bfsi", name: "Banking & Financial Services", icon: <Banknote className="w-4 h-4" /> },
    { slug: "retail", name: "Retail & E-commerce", icon: <ShoppingBag className="w-4 h-4" /> },
    { slug: "logistics", name: "Logistics & Supply Chain", icon: <Truck className="w-4 h-4" /> },
    { slug: "manufacturing", name: "Manufacturing", icon: <Factory className="w-4 h-4" /> },
    { slug: "tech", name: "Technology & SaaS", icon: <Building2 className="w-4 h-4" /> },
];

const engagementModels = [
    { title: "Build a product", copy: "Fixed-scope design and development to ship a production-ready system or software.", points: ["Sequenced roadmap", "Fixed price", "Outcome KPIs"] },
    { title: "Modernize a workflow", copy: "Integrate custom AI and automation accelerators into your existing operations.", points: ["Workflow audit", "Pre-built components", "Proven velocity"] },
    { title: "Hire specialized talent", copy: "Onboard vetted contract, C2C or W2 specialists matched to your stack in hours.", points: ["48-hour shortlists", "Skills-vetted candidates", "Managed compliance"] },
    { title: "Deploy AI agents", copy: "Configure and run custom, policy-governed agents for HR, finance, or customer ops.", points: ["Guardrail audits", "Agent orchestration", "System integrations"] },
];

export default function Home() {
    return (
        <div className="pqx-page-enter">
            <PageMeta
                title="AI, Talent & Product Engineering for Modern Enterprises"
                description="Perceptiqx is the AI-first partner enterprises use to ship intelligent products, automate operations and scale specialised technology teams — with measurable outcomes."
            />

            {/* ============== 1. HERO ============== */}
            <section
                className="relative pt-24 md:pt-28 pb-10 md:pb-14 overflow-hidden pqx-dark-panel min-h-screen flex items-center"
                data-testid="home-hero"
            >
                <div className="absolute inset-0 pqx-grid-bg-dark opacity-50" />
                <ParticleField density={26} />

                <div className="pqx-container relative z-10 grid lg:grid-cols-[1.05fr_1fr] gap-8 lg:gap-12 items-center w-full">
                    <div>
                        <span className="pqx-eyebrow !text-[#FF4040] pqx-reveal">
                            AI · Talent · Product Engineering
                        </span>
                        <h1
                            className="pqx-h1 mt-4 text-white pqx-reveal pqx-delay-1"
                            data-testid="hero-headline"
                        >
                            AI products,<br />
                            specialized teams,<br />
                            and enterprise software —{" "}
                            <span className="relative text-[#FF4040]">
                                shipped to production.
                                <svg
                                    className="absolute -bottom-2 left-0 w-full"
                                    height="10"
                                    viewBox="0 0 300 10"
                                    fill="none"
                                    preserveAspectRatio="none"
                                >
                                    <path
                                        d="M2 7 Q 80 1, 160 5 T 298 4"
                                        stroke="#FF4040"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        fill="none"
                                        opacity="0.7"
                                    />
                                </svg>
                            </span>
                        </h1>
                        <p className="mt-5 text-white/68 text-[15px] md:text-base lg:text-lg leading-relaxed max-w-xl pqx-reveal pqx-delay-2">
                            PerceptIQX helps enterprises move from AI ideas, hiring gaps, and broken workflows to production-ready products, teams, and automation.
                        </p>

                        <div className="mt-7 flex flex-wrap gap-3 pqx-reveal pqx-delay-3">
                            <Link
                                to="/ai-solutions"
                                data-testid="hero-cta-ai"
                                className="pqx-btn-accent"
                            >
                                Start with your roadmap
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/ai-solutions"
                                data-testid="hero-cta-talk"
                                className="pqx-btn-ghost-light"
                            >
                                Explore solutions
                            </Link>
                        </div>
                    </div>

                    {/* Hero visual */}
                    <div className="relative pqx-reveal pqx-delay-3 hidden sm:block">
                        <HeroAIVisual />
                    </div>
                </div>
            </section>

            {/* ============== 2. ENTERPRISE PROBLEM / CURRENT REALITY ============== */}
            <section className="pqx-section bg-white border-b border-[#303030]/8" data-testid="home-problem">
                <div className="pqx-container">
                    <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-14 items-center">
                        <div>
                            <span className="pqx-eyebrow">The Business Friction</span>
                            <h2 className="pqx-h2 mt-4 text-[#303030]">
                                Enterprise teams are trying to ship AI with broken operating systems.
                            </h2>
                            <p className="mt-5 text-[#303030]/70 text-base md:text-lg leading-relaxed">
                                Deploying production-ready AI isn't a models problem — it's an execution problem. Fragmented vendor lists, talent mismatch, and unintegrated systems create operational bottlenecks.
                            </p>
                        </div>
                        <div className="space-y-4">
                            {[
                                "AI pilots stuck in decks",
                                "Hiring gaps slowing delivery",
                                "Data scattered across tools",
                                "Manual reviews and approvals",
                                "No clear owner from idea to production"
                            ].map((point, index) => (
                                <div key={index} className="flex items-start gap-4 p-4 rounded-xl border border-[#303030]/8 bg-[#FAFAF8] hover:border-[#FF4040]/40 transition-all duration-300">
                                    <span className="w-6 h-6 shrink-0 rounded-full bg-[#FF4040]/10 text-[#FF4040] flex items-center justify-center font-mono text-[11px] font-bold">
                                        0{index + 1}
                                    </span>
                                    <p className="text-[14.5px] font-semibold text-[#303030] leading-tight mt-0.5">{point}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ============== 3. PERCEPTIQX OPERATING MODEL ============== */}
            <section className="pqx-section bg-[#FAFAF8] border-b border-[#303030]/8" data-testid="home-operating-model">
                <div className="pqx-container">
                    <div className="max-w-3xl mb-12">
                        <span className="pqx-eyebrow">Operating Model</span>
                        <h2 className="pqx-h2 mt-4 text-[#303030]">
                            One operating layer for AI, talent, and product delivery.
                        </h2>
                        <p className="mt-5 text-[#303030]/70 text-base md:text-lg leading-relaxed max-w-2xl">
                            PerceptIQX combines reusable AI accelerators, senior engineering squads, and specialized talent delivery into one execution system.
                        </p>
                    </div>

                    {/* Operating Layer Visual */}
                    <div className="relative p-8 rounded-3xl border border-[#303030]/10 bg-white shadow-[0_20px_50px_-30px_rgba(0,0,0,0.15)] flex flex-col items-center justify-center min-h-[420px] overflow-hidden">
                        {/* Connecting background decorative grids/rings */}
                        <div className="absolute inset-0 pqx-dot-bg opacity-35" />
                        <div className="absolute w-[280px] h-[280px] rounded-full border border-[#FF4040]/10 pointer-events-none animate-[spin_40s_linear_infinite]" />
                        <div className="absolute w-[380px] h-[380px] rounded-full border border-dashed border-[#303030]/5 pointer-events-none animate-[spin_60s_linear_infinite_reverse]" />

                        {/* Centered Node */}
                        <div className="relative z-10 w-64 p-6 rounded-2xl bg-[#303030] text-center shadow-[0_15px_30px_-10px_rgba(48,48,48,0.5)] border border-[#FF4040]/30 hover:border-[#FF4040]/80 transition-all duration-300">
                            <p className="text-[11px] uppercase tracking-[0.16em] text-[#FF4040] font-bold">Execution Engine</p>
                            <h3 className="text-lg font-bold text-white mt-1.5 leading-snug">PerceptIQX Operating Layer</h3>
                        </div>

                        {/* Surrounding Nodes Grid */}
                        <div className="relative z-10 mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 w-full max-w-4xl">
                            {[
                                "Talent Intelligence",
                                "Document Intelligence",
                                "Enterprise Agents",
                                "Product Engineering",
                                "Data Platforms",
                                "Cloud / DevOps",
                                "Recruiter Operations",
                                "Industry Workflows"
                            ].map((node) => (
                                <div key={node} className="flex items-center justify-center text-center p-4 rounded-xl border border-[#303030]/8 bg-white hover:border-[#FF4040] hover:shadow-[0_10px_25px_-15px_rgba(255,64,64,0.3)] transition-all duration-300">
                                    <span className="text-[13px] font-semibold text-[#303030]">{node}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ============== 4. AI WORKFLOW SIMULATOR ============== */}
            <WorkflowSimulator />

            {/* ============== 5. BEFORE vs AFTER ============== */}
            <BeforeAfter />

            {/* ============== 6. CORE SOLUTION LANES ============== */}
            <section className="pqx-section bg-white border-b border-[#303030]/8" data-testid="home-solution-lanes">
                <div className="pqx-container">
                    <div className="max-w-3xl mb-12">
                        <span className="pqx-eyebrow">Solutions</span>
                        <h2 className="pqx-h2 mt-4 text-[#303030]">
                            Four ways we help enterprises ship faster.
                        </h2>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {[
                            {
                                title: "AI Products & Accelerators",
                                kicker: "TalentIQX · RecruitIQX · DocuIQX · AgentIQX",
                                desc: "Deploy pre-built enterprise frameworks that solve sourcing, extraction, and agentic workflows in days.",
                                to: "/products",
                            },
                            {
                                title: "Specialized Talent Delivery",
                                kicker: "Tech staffing · C2C · contract hiring",
                                desc: "Source, score, and onboard vetted AI, data, and software engineering talent in hours.",
                                to: "/talent-solutions",
                            },
                            {
                                title: "Product Engineering",
                                kicker: "SaaS · Portals · Platforms · Dashboards",
                                desc: "Design and build production-ready custom applications, data operations, and cloud platforms.",
                                to: "/product-engineering",
                            },
                            {
                                title: "Enterprise Workflow Automation",
                                kicker: "Agents · Document workflows · Data ops",
                                desc: "Orchestrate multi-step processes, integrations, and policy-governed automated agents.",
                                to: "/ai-solutions",
                            },
                        ].map((lane, i) => (
                            <Link
                                key={lane.title}
                                to={lane.to}
                                className="group relative pqx-card-elevated !p-7 flex flex-col"
                            >
                                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#303030]/45">
                                    0{i + 1}
                                </span>
                                <h3 className="pqx-display mt-5 text-xl text-[#303030] leading-snug">
                                    {lane.title}
                                </h3>
                                <p className="mt-1 text-[11px] uppercase tracking-[0.16em] text-[#FF4040] font-semibold">
                                    {lane.kicker}
                                </p>
                                <p className="mt-4 text-[#303030]/72 text-[14px] leading-relaxed flex-1">
                                    {lane.desc}
                                </p>
                                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#303030] group-hover:text-[#FF4040] transition-colors">
                                    Learn more
                                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============== 7. REUSABLE ACCELERATORS PREVIEW ============== */}
            <section className="pqx-section bg-[#FAFAF8] border-b border-[#303030]/8" data-testid="home-products">
                <div className="pqx-container">
                    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
                        <div className="max-w-2xl">
                            <span className="pqx-eyebrow">Accelerators</span>
                            <h2 className="pqx-h2 mt-4 text-[#303030]">
                                Reusable accelerators, not blank-slate projects.
                            </h2>
                        </div>
                        <Link to="/products" className="pqx-link" data-testid="home-products-all">
                            All accelerators <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                        {accelerators.map((p, i) => (
                            <Link
                                to={`/products/${p.slug}`}
                                key={p.name}
                                className="group relative pqx-card-elevated !p-6 flex flex-col overflow-hidden"
                            >
                                <div
                                    className="absolute inset-x-0 -top-12 h-32 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
                                    style={{
                                        background: "radial-gradient(60% 80% at 50% 0%, rgba(255,64,64,0.18), transparent 70%)",
                                    }}
                                />
                                <div className="relative">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#303030]/45">
                                        0{i + 1} · Accelerator
                                    </span>
                                    <div className="mt-4 w-11 h-11 grid place-items-center rounded-xl bg-[#303030]/5 text-[#303030] group-hover:bg-[#FF4040] group-hover:text-white transition-colors">
                                        {p.icon}
                                    </div>
                                    <h3 className="pqx-display text-lg text-[#303030] mt-5 leading-snug">
                                        {p.name}
                                    </h3>
                                    <p className="text-[11px] uppercase tracking-[0.16em] text-[#FF4040] font-semibold mt-1">
                                        {p.tag}
                                    </p>
                                    <p className="text-[13.5px] text-[#303030]/70 leading-relaxed mt-3 flex-1">
                                        {p.copy}
                                    </p>
                                    <span className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold text-[#303030] group-hover:text-[#FF4040] transition-colors">
                                        Explore
                                        <ArrowUpRight className="w-4 h-4" />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============== 8. INDUSTRY CONTEXT PREVIEW ============== */}
            <section className="pqx-section bg-white border-b border-[#303030]/8" data-testid="home-industries">
                <div className="pqx-container">
                    <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-14 items-start">
                        <div>
                            <span className="pqx-eyebrow">Industries we serve</span>
                            <h2 className="pqx-h2 mt-4 text-[#303030]">
                                Built with domain context, not generic AI.
                            </h2>
                            <p className="mt-5 text-[#303030]/70 text-base md:text-lg leading-relaxed max-w-md">
                                Every solution we deliver respects your regulatory, operational and
                                customer realities — because the people building it are specialists
                                in your industry.
                            </p>
                            <Link to="/industries" className="mt-7 inline-flex pqx-btn-primary">
                                Explore industries
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
                            {industries.map((ind) => (
                                <Link
                                    to={`/industries/${ind.slug}`}
                                    key={ind.name}
                                    data-testid={`home-industry-${ind.slug}`}
                                    className="group flex items-center gap-4 rounded-2xl bg-white border border-[#303030]/8 p-5 hover:border-[#FF4040]/40 hover:shadow-[0_20px_50px_-30px_rgba(255,64,64,0.35)] transition-all hover:-translate-y-0.5"
                                >
                                    <span className="w-10 h-10 grid place-items-center rounded-xl bg-[#303030]/5 text-[#303030] group-hover:bg-[#FF4040] group-hover:text-white transition-colors">
                                        {ind.icon}
                                    </span>
                                    <div className="flex-1">
                                        <p className="text-[14.5px] font-semibold text-[#303030]">
                                            {ind.name}
                                        </p>
                                        <p className="text-[11.5px] text-[#303030]/55 mt-0.5">
                                            AI · Engineering · Talent
                                        </p>
                                    </div>
                                    <ArrowUpRight className="w-4 h-4 text-[#303030]/40 group-hover:text-[#FF4040]" />
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ============== 9. ENGAGEMENT MODEL ============== */}
            <section className="pqx-section bg-[#FAFAF8] border-b border-[#303030]/8" data-testid="home-engagement">
                <div className="pqx-container">
                    <div className="max-w-3xl">
                        <span className="pqx-eyebrow">Engagement models</span>
                        <h2 className="pqx-h2 mt-4 text-[#303030]">
                            Work with us the way your team operates.
                        </h2>
                    </div>

                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {engagementModels.map((m, i) => (
                            <div
                                key={m.title}
                                data-testid={`engagement-card-${i}`}
                                className="pqx-card-elevated !p-7 bg-white"
                            >
                                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#303030]/45">
                                    Model 0{i + 1}
                                </span>
                                <h3 className="pqx-display text-2xl text-[#303030] mt-3">
                                    {m.title}
                                </h3>
                                <p className="mt-3 text-[#303030]/70 text-[15px] leading-relaxed">
                                    {m.copy}
                                </p>
                                <ul className="mt-5 space-y-2.5">
                                    {m.points.map((p) => (
                                        <li key={p} className="flex items-start gap-2.5 text-sm text-[#303030]/80">
                                            <Check className="w-4 h-4 text-[#FF4040] mt-0.5 shrink-0" />
                                            {p}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============== 10. FINAL CTA ============== */}
            <FinalCTA
                title="Bring us a roadmap, requirement, or broken workflow."
                description="We’ll help you turn it into a delivery plan, AI workflow, product build, or vetted team."
                primary={{ to: "/contact", label: "Start a conversation" }}
                secondary={{ to: "/ai-solutions", label: "Explore AI solutions" }}
            />
        </div>
    );
}
